import React, { useState, useEffect } from 'react';
import { supabase, signOut } from '../lib/supabaseClient';
import PrivacyPolicy from './PrivacyPolicy';
import TermsOfService from './TermsOfService';

const JOURNEY_OPTIONS = [
  {
    key: 'noticing_symptoms',
    chapter: '01',
    label: 'Something feels wrong',
    subtitle: "You're noticing changes but aren't sure what's happening yet",
  },
  {
    key: 'seeking_diagnosis',
    chapter: '02',
    label: 'Getting answers',
    subtitle: 'Looking for a diagnosis and trying to be taken seriously',
  },
  {
    key: 'preparing_appointment',
    chapter: '02',
    label: 'Preparing for an appointment',
    subtitle: 'Want to walk in with a clear, structured picture',
  },
  {
    key: 'started_hrt',
    chapter: '03',
    label: 'Just started HRT',
    subtitle: "Tracking how it's working for you",
  },
  {
    key: 'adjusting_hrt',
    chapter: '03',
    label: 'Adjusting my HRT',
    subtitle: 'Finding the right dose or type',
  },
  {
    key: 'managing_symptoms',
    chapter: '04',
    label: 'Finding my normal',
    subtitle: "Maintaining and keeping track of what's working",
  },
];

function getWelcomeMessage(journeyKey) {
  const messages = {
    noticing_symptoms: "We'll help you build a clear picture of what's happening. Tracking daily means you'll have a clear record — not just memory — when you talk to your doctor.",
    seeking_diagnosis: "You're in the right place. Consistent tracking gives you a clear record of how you've been to share with your doctor. Let's build it together.",
    started_hrt: "Tracking through an HRT journey is one of the most powerful things you can do. We'll show you exactly when things start to shift.",
    adjusting_hrt: "Dose adjustments are hard to navigate without data. We'll help you see clearly what's changing and when — so you can advocate for yourself.",
    managing_symptoms: "Staying consistent is everything. We'll keep spotting patterns and make sure your next appointment is always backed by real data.",
    preparing_appointment: "Perfect. Log daily and we'll help you walk in with a clear, professional summary your doctor can actually use.",
  };
  return messages[journeyKey] || "Let's get started.";
}

function TrackerGate({ onEmailSubmit, onJoinWaitlist }) {
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState(
    localStorage.getItem('apptnote_user_email') || ''
  );
  const [error, setError] = useState('');
  const [showPrivacy, setShowPrivacy] = useState(false);
  const [showTerms, setShowTerms] = useState(false);
  const [checking, setChecking] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [journeyStage, setJourneyStage] = useState('');
  const [journeySubStep, setJourneySubStep] = useState('stage');
  const [hrtType, setHrtType] = useState('');
  const [hrtStartMonth, setHrtStartMonth] = useState('');
  const [hrtChanges, setHrtChanges] = useState('');
  const [cleanEmail, setCleanEmail] = useState('');
  const [sessionToken, setSessionToken] = useState('');
  const [code, setCode] = useState('');
  const [resendIn, setResendIn] = useState(0);

  // Countdown before another code can be requested
  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const t = setTimeout(() => setResendIn((n) => n - 1), 1000);
    return () => clearTimeout(t);
  }, [resendIn]);

  // Step 1: send a one-time sign-in code to the email address.
  const sendCode = async (address) => {
    if (!supabase) {
      setError('Sign-in is temporarily unavailable. Please try again later.');
      return false;
    }
    const { error: otpError } = await supabase.auth.signInWithOtp({
      email: address,
      // ApptNote is closing: existing users can sign in, no new accounts.
      options: { shouldCreateUser: false },
    });
    if (otpError) {
      const signupsClosed = /signups? not allowed|user not found/i.test(otpError.message || '');
      setError(otpError.status === 429
        ? 'Please wait a minute before requesting another code.'
        : signupsClosed
          ? 'ApptNote is closing on 1 December 2026 and is no longer accepting new sign-ups. If you already have an account, please check the email address.'
          : 'We couldn\'t send your code. Please check the email address and try again.');
      return false;
    }
    setResendIn(60);
    return true;
  };

  const handleEmailSubmit = async () => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const trimmed = email.trim();
    if (!trimmed) { setError('Please enter your email'); return; }
    // Phones often add a trailing space when autocompleting, so check the trimmed value.
    if (trimmed.length > 254 || !emailRegex.test(trimmed)) { setError('Please enter a valid email'); return; }
    if (!consentGiven) { setError('Please tick the box to agree and continue'); return; }

    setChecking(true);
    setError('');
    const emailClean = email.toLowerCase().trim();
    try {
      if (await sendCode(emailClean)) {
        setCleanEmail(emailClean);
        setCode('');
        setStep('code');
      }
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const handleResend = async () => {
    if (resendIn > 0 || checking) return;
    setError('');
    setChecking(true);
    try {
      await sendCode(cleanEmail);
    } finally {
      setChecking(false);
    }
  };

  // Step 2: verify the code, then record consent and load the profile.
  const handleVerifyCode = async () => {
    const digits = code.replace(/\D/g, '');
    if (digits.length < 6) { setError('Please enter the code from your email'); return; }

    setChecking(true);
    setError('');
    try {
      const { data, error: verifyError } = await supabase.auth.verifyOtp({
        email: cleanEmail,
        token: digits,
        type: 'email',
      });
      const token = data?.session?.access_token;
      if (verifyError || !token) {
        setError('That code didn\'t work or has expired. Check the latest email, or request a new code.');
        return;
      }

      const authRes = await fetch('/api/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ consent: true }),
      });
      if (!authRes.ok) {
        await signOut();
        setError('Something went wrong signing you in. Please try again.');
        setStep(1);
        return;
      }

      const { journey_stage: dbJourneyStage } = await authRes.json();
      try {
        localStorage.setItem('apptnote_user_email', cleanEmail);
        localStorage.setItem('apptnote_profile_ok', cleanEmail);
      } catch (e) { /* storage unavailable — session still works for this visit */ }
      setSessionToken(token);

      if (dbJourneyStage) {
        try { localStorage.setItem('apptnote_journey_stage', dbJourneyStage); } catch (e) { /* ignore */ }
        onEmailSubmit(cleanEmail, token, dbJourneyStage);
        return;
      }
      setStep(2);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setChecking(false);
    }
  };

  const HRT_STAGES = ['started_hrt', 'adjusting_hrt'];
  const isHRTStage = HRT_STAGES.includes(journeyStage);

  const handleJourneyContinue = () => {
    if (isHRTStage && journeySubStep === 'stage') {
      setJourneySubStep('hrt_details');
      return;
    }
    handleJourneySubmit();
  };

  const handleJourneySubmit = async () => {
    if (journeyStage) localStorage.setItem('apptnote_journey_stage', journeyStage);
    const hrt_details = isHRTStage
      ? { type: hrtType || null, start_month: hrtStartMonth || null, recent_changes: hrtChanges || null }
      : null;
    try {
      await fetch('/api/save-journey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${sessionToken}` },
        body: JSON.stringify({ journey_stage: journeyStage || null, hrt_details }),
      });
    } catch (err) {
      console.error('Failed to save journey stage:', err);
    }
    onEmailSubmit(cleanEmail, sessionToken, journeyStage || null);
  };

  const handleKeyPress = (e) => { if (e.key === 'Enter') handleEmailSubmit(); };

  // ── Step 1 — Email + consent ──────────────────────────────────────────────
  if (step === 1) {
    return (
      <div style={styles.container}>
        <div style={styles.inner}>
          <div style={styles.header}>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
          <div style={styles.content}>
            <div style={styles.stepPill}>Start your story</div>
            <h1 style={styles.title}>
              Your first chapter<br />
              <em style={styles.titleEm}>starts here.</em>
            </h1>
            <p style={styles.subtitle}>
              Enter your email and we'll send you a one-time sign-in code.
              No password to remember.
            </p>
            <div style={styles.formSection}>
              <input
                type="email"
                inputMode="email"
                autoComplete="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
                maxLength={254}
                placeholder="your@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onKeyPress={handleKeyPress}
                style={styles.input}
                autoFocus
              />
              <div style={styles.checkboxRow}>
                <input
                  type="checkbox"
                  id="consent"
                  checked={consentGiven}
                  onChange={(e) => setConsentGiven(e.target.checked)}
                  style={styles.checkbox}
                />
                <label htmlFor="consent" style={styles.checkboxLabel}>
                  I agree to the{' '}
                  <button type="button" style={styles.consentLinkButton}
                    onClick={(e) => { e.preventDefault(); setShowTerms(true); }}>
                    Terms of Service
                  </button>{' '}and{' '}
                  <button type="button" style={styles.consentLinkButton}
                    onClick={(e) => { e.preventDefault(); setShowPrivacy(true); }}>
                    Privacy Policy
                  </button>, and I consent to ApptNote processing my health data
                  (symptoms, mood, energy, sleep and stress scores) to create my
                  summaries and reports. I understand ApptNote is not a medical
                  service. I can delete my account and data at any time.
                </label>
              </div>
              {error && <p style={styles.error}>{error}</p>}
              <button
                style={checking ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
                onClick={handleEmailSubmit}
                disabled={checking}
              >
                {checking ? 'Sending your code…' : 'Send my code →'}
              </button>
            </div>
            <p style={styles.disclaimer}>
              For adults 18 and over. Encrypted in transit and at rest.
            </p>
          </div>
        </div>
        {showPrivacy && <PrivacyPolicy onClose={() => setShowPrivacy(false)} />}
        {showTerms && <TermsOfService onClose={() => setShowTerms(false)} />}
      </div>
    );
  }

  // ── Step 1b — Enter sign-in code ──────────────────────────────────────────
  if (step === 'code') {
    return (
      <div style={styles.container}>
        <div style={styles.inner}>
          <div style={styles.header}>
            <span style={styles.logoAppt}>Appt</span>
            <span style={styles.logoNote}>Note</span>
          </div>
          <div style={styles.content}>
            <div style={styles.stepPill}>Check your email</div>
            <h1 style={styles.title}>
              Enter your<br />
              <em style={styles.titleEm}>sign-in code.</em>
            </h1>
            <p style={styles.subtitle}>
              We've sent a code to <strong>{cleanEmail}</strong>. It expires in
              10 minutes. If you can't see it, check your spam folder.
            </p>
            <div style={styles.formSection}>
              <input
                type="text"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                maxLength={10}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                onKeyPress={(e) => { if (e.key === 'Enter') handleVerifyCode(); }}
                style={{ ...styles.input, ...styles.codeInput }}
                aria-label="Sign-in code"
                autoFocus
              />
              {error && <p style={styles.error}>{error}</p>}
              <button
                style={checking ? { ...styles.button, ...styles.buttonDisabled } : styles.button}
                onClick={handleVerifyCode}
                disabled={checking}
              >
                {checking ? 'Checking…' : 'Sign in →'}
              </button>
              <div style={styles.codeLinks}>
                <button
                  style={resendIn > 0 ? { ...styles.disclaimerLink, ...styles.linkDisabled } : styles.disclaimerLink}
                  onClick={handleResend}
                  disabled={resendIn > 0 || checking}
                >
                  {resendIn > 0 ? `Send a new code in ${resendIn}s` : 'Send a new code'}
                </button>
                <button
                  style={styles.disclaimerLink}
                  onClick={() => { setStep(1); setError(''); setCode(''); }}
                >
                  Use a different email
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ── Step 2 — Journey stage ────────────────────────────────────────────────
  return (
    <div style={styles.container}>
      <div style={styles.inner}>
        <div style={styles.header}>
          <span style={styles.logoAppt}>Appt</span>
          <span style={styles.logoNote}>Note</span>
        </div>
        <div style={styles.content}>

          <div style={styles.progressRow}>
            <div style={styles.progressDotDone} />
            <div style={styles.progressLine} />
            <div style={styles.progressDotActive} />
          </div>

          {journeySubStep === 'stage' ? (
            <>
              <div style={styles.stepPill}>Your chapter</div>
              <h1 style={styles.title}>
                Where are you<br />
                <em style={styles.titleEm}>right now?</em>
              </h1>
              <p style={styles.subtitle}>
                This helps us tailor your experience and insights to exactly
                where you are in your journey.
              </p>

              <div style={styles.journeyOptions}>
                {JOURNEY_OPTIONS.map(({ key, chapter, label, subtitle }) => (
                  <button
                    key={key}
                    style={{
                      ...styles.journeyOption,
                      ...(journeyStage === key ? styles.journeyOptionActive : {})
                    }}
                    onClick={() => setJourneyStage(key)}
                  >
                    <div style={{
                      ...styles.chapterBadge,
                      ...(journeyStage === key ? styles.chapterBadgeActive : {})
                    }}>
                      {chapter}
                    </div>
                    <div style={styles.journeyText}>
                      <div style={styles.journeyLabel}>{label}</div>
                      <div style={styles.journeySubtitle}>{subtitle}</div>
                    </div>
                    {journeyStage === key && (
                      <span style={styles.journeyTick}>✓</span>
                    )}
                  </button>
                ))}
              </div>

              {journeyStage && (
                <div style={styles.welcomeMessage}>
                  <p style={styles.welcomeMessageText}>
                    {getWelcomeMessage(journeyStage)}
                  </p>
                </div>
              )}

              <button
                style={{
                  ...styles.button,
                  ...(journeyStage ? {} : styles.buttonMuted)
                }}
                onClick={journeyStage ? handleJourneyContinue : handleJourneySubmit}
              >
                {journeyStage
                  ? (isHRTStage ? 'Continue →' : 'Start my journey →')
                  : 'Skip and start tracking →'}
              </button>
            </>
          ) : (
            <>
              <button style={styles.backLink} onClick={() => setJourneySubStep('stage')}>
                ← Back
              </button>
              <div style={{ ...styles.stepPill, marginTop: '16px' }}>HRT details</div>
              <h1 style={styles.title}>
                Tell us a little<br />
                <em style={styles.titleEm}>about your HRT.</em>
              </h1>
              <p style={styles.subtitle}>
                Optional — but it helps us show you a clearer before-and-after picture.
              </p>

              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>What type of HRT?</label>
                <div style={styles.pillRow}>
                  {['Patch', 'Gel', 'Spray', 'Pill', 'Combined', 'Other'].map((label) => {
                    const key = label.toLowerCase();
                    return (
                      <button
                        key={key}
                        style={{
                          ...styles.pill,
                          ...(hrtType === key ? styles.pillActive : {})
                        }}
                        onClick={() => setHrtType(key)}
                      >
                        {label}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>When did you start?</label>
                <input
                  type="month"
                  value={hrtStartMonth}
                  onChange={(e) => setHrtStartMonth(e.target.value)}
                  style={styles.input}
                  max={new Date().toISOString().slice(0, 7)}
                />
              </div>

              <div style={styles.fieldGroup}>
                <label style={styles.fieldLabel}>Any recent changes? <span style={{ fontWeight: '400', color: '#6c8396' }}>(optional)</span></label>
                <input
                  type="text"
                  value={hrtChanges}
                  onChange={(e) => setHrtChanges(e.target.value)}
                  placeholder="e.g. switched from patch to gel"
                  style={styles.input}
                  maxLength={120}
                />
              </div>

              <button style={styles.button} onClick={handleJourneySubmit}>
                Start my journey →
              </button>
            </>
          )}

        </div>
      </div>
    </div>
  );
}

const serif = "'DM Serif Display', Georgia, serif";
const sans = "'DM Sans', system-ui, sans-serif";

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#FAF7F2',
    fontFamily: sans,
    display: 'flex',
    flexDirection: 'column',
  },
  inner: {
    maxWidth: '480px',
    margin: '0 auto',
    padding: '0 24px',
    width: '100%',
  },
  header: {
    padding: '28px 0 24px',
    borderBottom: '1px solid rgba(44,44,42,0.08)',
    marginBottom: '48px',
    display: 'flex',
    alignItems: 'baseline',
    gap: '1px',
  },
  logoAppt: { fontSize: '22px', fontWeight: '700', color: '#2C2C2A', fontFamily: sans },
  logoNote: { fontSize: '22px', fontWeight: '700', color: '#D4A5A5', fontFamily: sans },
  content: { textAlign: 'center' },

  stepPill: {
    display: 'inline-block',
    fontSize: '11px',
    fontWeight: '600',
    letterSpacing: '0.12em',
    textTransform: 'uppercase',
    color: '#7A9E87',
    marginBottom: '20px',
    padding: '5px 14px',
    border: '1px solid rgba(122,158,135,0.3)',
    borderRadius: '100px',
  },
  title: {
    fontFamily: serif,
    fontSize: '40px',
    fontWeight: '400',
    color: '#1a1a18',
    lineHeight: '1.1',
    marginBottom: '16px',
    letterSpacing: '-0.02em',
  },
  titleEm: {
    fontStyle: 'italic',
    color: '#7A9E87',
  },
  subtitle: {
    fontSize: '16px',
    color: '#4a6070',
    lineHeight: '1.65',
    maxWidth: '380px',
    margin: '0 auto 32px',
    fontWeight: '300',
  },

  formSection: { marginBottom: '20px' },
  input: {
    width: '100%',
    padding: '15px 18px',
    fontSize: '16px',
    border: '1.5px solid rgba(44,44,42,0.12)',
    borderRadius: '10px',
    marginBottom: '12px',
    boxSizing: 'border-box',
    fontFamily: sans,
    backgroundColor: 'white',
    color: '#2C2C2A',
    outline: 'none',
  },
  button: {
    width: '100%',
    padding: '16px',
    backgroundColor: '#2C2C2A',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontSize: '16px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: sans,
    marginTop: '20px',
  },
  buttonDisabled: { backgroundColor: '#a0a09e', cursor: 'not-allowed' },
  codeInput: {
    textAlign: 'center', fontSize: '24px', letterSpacing: '0.4em', fontWeight: '600',
  },
  codeLinks: {
    display: 'flex', justifyContent: 'space-between', gap: '12px',
    flexWrap: 'wrap', marginTop: '16px',
  },
  linkDisabled: { cursor: 'default', textDecoration: 'none', opacity: 0.7 },
  buttonMuted: { backgroundColor: '#a0a09e' },

  error: { color: '#c44444', fontSize: '14px', marginBottom: '8px', textAlign: 'left' },

  checkboxRow: {
    display: 'flex', alignItems: 'flex-start', gap: '12px',
    textAlign: 'left', marginBottom: '4px', marginTop: '4px',
  },
  checkbox: {
    marginTop: '3px', flexShrink: 0, width: '18px', height: '18px',
    accentColor: '#7A9E87', cursor: 'pointer',
  },
  checkboxLabel: { fontSize: '13px', color: '#4a6070', lineHeight: '1.6', cursor: 'pointer' },
  consentLink: { color: '#7A9E87', textDecoration: 'underline' },
  consentLinkButton: { background: 'none', border: 'none', padding: 0, font: 'inherit', color: '#7A9E87', textDecoration: 'underline', cursor: 'pointer' },

  disclaimer: {
    fontSize: '12px', color: '#6c8396', lineHeight: '1.6',
    maxWidth: '380px', margin: '16px auto 0',
  },
  disclaimerLink: {
    background: 'none', border: 'none', color: '#6c8396',
    textDecoration: 'underline', cursor: 'pointer', padding: '0',
    fontFamily: sans, fontSize: '12px',
  },

  // Progress
  progressRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    gap: '8px', marginBottom: '32px',
  },
  progressDotDone: {
    width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#7A9E87',
  },
  progressLine: { width: '40px', height: '2px', backgroundColor: 'rgba(44,44,42,0.1)' },
  progressDotActive: {
    width: '12px', height: '12px', borderRadius: '50%',
    backgroundColor: '#D4A5A5',
  },

  // Journey options
  journeyOptions: {
    display: 'flex', flexDirection: 'column', gap: '8px',
    marginBottom: '20px', textAlign: 'left',
  },
  journeyOption: {
    display: 'flex', alignItems: 'center', gap: '14px',
    padding: '14px 16px', backgroundColor: 'white',
    border: '1.5px solid rgba(44,44,42,0.08)', borderRadius: '12px',
    cursor: 'pointer', fontFamily: sans, textAlign: 'left',
    transition: 'border-color 0.15s ease',
  },
  journeyOptionActive: { backgroundColor: '#fdf9f5', borderColor: '#D4A5A5' },
  chapterBadge: {
    fontFamily: serif,
    fontSize: '18px',
    fontWeight: '400',
    color: '#D4A5A5',
    opacity: '0.5',
    flexShrink: 0,
    width: '32px',
    textAlign: 'center',
  },
  chapterBadgeActive: { opacity: '1' },
  journeyText: { flex: 1 },
  journeyLabel: { fontSize: '15px', fontWeight: '600', color: '#2C2C2A', marginBottom: '2px' },
  journeySubtitle: { fontSize: '12px', color: '#6c8396', lineHeight: '1.4' },
  journeyTick: { color: '#7A9E87', fontWeight: '700', fontSize: '14px', flexShrink: 0 },

  // Welcome message
  welcomeMessage: {
    backgroundColor: 'white',
    border: '1px solid rgba(122,158,135,0.25)',
    borderLeft: '3px solid #7A9E87',
    borderRadius: '8px',
    padding: '14px 18px',
    marginBottom: '4px',
    textAlign: 'left',
  },
  welcomeMessageText: {
    fontSize: '14px', color: '#2C2C2A', lineHeight: '1.7',
    margin: 0, fontStyle: 'italic', fontWeight: '300',
  },

  // HRT details
  backLink: {
    background: 'none', border: 'none', color: '#6c8396',
    fontSize: '14px', cursor: 'pointer', fontFamily: sans,
    padding: '0', fontWeight: '500',
  },
  fieldGroup: { marginBottom: '20px', textAlign: 'left' },
  fieldLabel: {
    display: 'block', fontSize: '13px', fontWeight: '600',
    color: '#2C2C2A', marginBottom: '10px',
  },
  pillRow: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  pill: {
    padding: '8px 16px',
    backgroundColor: 'white',
    border: '1.5px solid rgba(44,44,42,0.12)',
    borderRadius: '100px', fontSize: '13px', fontWeight: '500',
    color: '#4a6070', cursor: 'pointer', fontFamily: sans,
  },
  pillActive: {
    backgroundColor: '#D4A5A5',
    borderColor: '#D4A5A5',
    color: 'white',
  },
};

export default TrackerGate;
